import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Radio } from "lucide-react";
import { fetchDxyTicker } from "@/lib/api";
import { instrumentOpen, anyMarketOpen } from "@/lib/marketHours";

const TICKER_CSS = `
@keyframes dxytickerscroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.dpt-track { animation: dxytickerscroll 60s linear infinite; }
.dpt-wrap:hover .dpt-track { animation-play-state: paused; }
`;

export const canonicalPair = (s) => {
  const n = String(s).toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (["GOLD", "XAU", "XAUUSD", "GC"].includes(n)) return "XAUUSD";
  if (["WTI", "OIL", "CRUDE", "WTICRUDE", "CL"].includes(n)) return "WTI";
  if (["DXY", "DOLLAR", "USDX"].includes(n)) return "DXY";
  return n;
};

function Spark({ values }) {
  if (!values || values.length < 2) return <span className="inline-block w-11" />;
  const w = 44, h = 14;
  const min = Math.min(...values), max = Math.max(...values), rng = (max - min) || 1;
  const pts = values
    .map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - ((v - min) / rng) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
    </svg>
  );
}

function goToPair(key) {
  const el = document.getElementById(`dxy-pair-${canonicalPair(key)}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("pair-flash");
  setTimeout(() => el.classList.remove("pair-flash"), 1200);
}

function QuoteItem({ q, open }) {
  const chg = q.chg_pct;
  const cls = chg == null ? "text-zinc-500" : chg > 0 ? "text-pos" : chg < 0 ? "text-neg" : "text-zinc-500";
  const arrow = chg == null ? "" : chg > 0 ? "▲" : chg < 0 ? "▼" : "▬";
  return (
    <button
      type="button"
      onClick={() => goToPair(q.key)}
      className="inline-flex items-center gap-2 shrink-0 hover:opacity-100 opacity-90 group"
      title={`Jump to ${q.label} · market ${open ? "open" : "closed"}`}
      data-testid={`dxy-ticker-item-${q.key}`}
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${open ? "bg-pos animate-pulse" : "bg-neg"}`}
        data-testid={`dxy-ticker-status-${q.key}`}
        aria-label={open ? "market open" : "market closed"}
      />
      <span className="text-[11px] uppercase tracking-widest text-zinc-400 group-hover:text-amber-term transition-colors">{q.label}</span>
      <span className="text-[11px] font-mono tabular-nums text-zinc-200">{q.price}</span>
      <span className={cls}><Spark values={q.spark} /></span>
      <span className={`text-[11px] font-mono tabular-nums ${cls}`}>
        {arrow} {chg == null ? "n/a" : `${chg > 0 ? "+" : ""}${chg.toFixed(2)}%`}
      </span>
    </button>
  );
}

export default function DxyPriceTicker() {
  const q = useQuery({
    queryKey: ["dxy-ticker"],
    queryFn: fetchDxyTicker,
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60,
    refetchOnWindowFocus: false,
  });
  const quotes = q.data?.quotes || [];

  // Re-evaluate market open/closed every 30s so the dots flip at session boundaries.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const anyOpen = anyMarketOpen(quotes.map((x) => x.key), now);

  return (
    <div className="dpt-wrap border-b border-zinc-800 bg-black overflow-hidden" data-testid="dxy-price-ticker">
      <style>{TICKER_CSS}</style>
      <div className="flex items-stretch">
        <div
          className="flex items-center gap-1.5 px-3 border-r border-zinc-800 bg-[#09090B] shrink-0 z-10"
          title={anyOpen ? "Markets open" : "All markets closed"}
          data-testid="dxy-ticker-market-state"
        >
          <Radio
            size={11}
            strokeWidth={1.75}
            className={anyOpen ? "text-pos animate-pulse" : "text-neg"}
          />
          <span className="text-[11px] uppercase tracking-widest text-zinc-400">Live FX</span>
        </div>
        <div className="relative flex-1 overflow-hidden py-1.5">
          {q.isLoading || quotes.length === 0 ? (
            <span className="px-3 text-[11px] uppercase tracking-widest text-zinc-600">Fetching live prices…</span>
          ) : (
            <div className="dpt-track flex items-center gap-8 whitespace-nowrap pl-8 will-change-transform" data-testid="dxy-ticker-track">
              {quotes.concat(quotes).map((item, i) => (
                <QuoteItem key={`${item.key}-${i}`} q={item} open={instrumentOpen(item.key, now)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
